// absolute imports
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo } from 'react';
import {
  CUSTOMER_SUBSCRIPTION,
  PAYMENT_SOURCE_QUERY,
  PLANS,
} from '../../data/apollo/queries';

// relative imports
import { primaryAccountIdVar } from '../../data/apollo/cache/reactiveVars';
import {
  CUSTOMER_CHECKOUT_SESSION,
  CUSTOMER_ONE_TIME_CHECKOUT,
  CUSTOMER_PORTAL_SESSION,
  CUSTOMER_UPGRADE_SESSION,
} from '../../data/apollo/mutations';
import {
  checkFeaturePrimitiveValue,
  isModuleEnabled,
} from '../../data/app-config/configs/configUtilities';
import {
  handleOneTimeCheckout,
  handlePlanCheckoutSession,
  handlePlanUpgradeSession,
  handlePortalOpenSection,
} from '../billing/chargebee/chargebeeClient';
import { useAccountConfig } from './AccountConfigProvider';
import { useAccount } from './AccountProvider';

// helper functions
export function getRemainingWallets(usageWallets, plan, addons) {
  const realUsage = usageWallets;
  const addonWallets = addons && addons.extraWallets ? addons.extraWallets : 0;
  const validWallets = addonWallets + plan.numberOfWallets;
  return validWallets - realUsage;
}

export function getRemainingExchanges(usageExchanges, plan, addons) {
  const realUsage = usageExchanges;
  const addonExchanges =
    addons && addons.extraExchanges ? addons.extraExchanges : 0;
  const validExchanges = addonExchanges + plan.numberOfExchangeAccounts;
  return validExchanges - realUsage;
}

const billingErrorHandler = () =>
  enqueueSnackbar('Unable to open billing portal. Please try again.', { variant: 'error' });

// create context for provider
export const BillingContext = createContext();
export const BillingActionsContext = createContext();
export const useBilling = () => useContext(BillingContext);
export const useBillingActions = () => useContext(BillingActionsContext);

export const BillingProvider = ({ children }) => {
  const { currentAccount, loadingAccountInfo } = useAccount();
  const primaryAccountId = useReactiveVar(primaryAccountIdVar);
  const { plan, disabled = false, usage } = currentAccount;
  const { loadingAccountConfig } = useAccountConfig();

  const billingUnavailable = useMemo(
    () =>
      !primaryAccountId ||
      loadingAccountInfo ||
      loadingAccountConfig ||
      disabled ||
      !isModuleEnabled({ moduleName: 'billing' }) ||
      checkFeaturePrimitiveValue({
        moduleName: 'billing',
        featureName: 'billingFormat',
        value: 'admin-tax',
      }),
    [primaryAccountId, loadingAccountInfo, loadingAccountConfig, disabled],
  );
  const { data: { getPlans: plans = [] } = {}, loading: loadingPlans } =
    useQuery(PLANS, {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      skip: billingUnavailable,
    });

  const [makeCustomerPortal] = useMutation(CUSTOMER_PORTAL_SESSION, { onError: billingErrorHandler });
  const [makeCheckoutPortal] = useMutation(CUSTOMER_CHECKOUT_SESSION, { onError: billingErrorHandler });
  const [makeUpgradePortal] = useMutation(CUSTOMER_UPGRADE_SESSION, { onError: billingErrorHandler });
  const [makeOneTimeCheckout] = useMutation(CUSTOMER_ONE_TIME_CHECKOUT, { onError: billingErrorHandler });

  const {
    data: { getChargeBeePaymentSource: paymentSource } = {},
    loading: loadingPaymentSource,
    refetch: refetchPaymentSource,
  } = useQuery(PAYMENT_SOURCE_QUERY, {
    variables: {
      accountId: primaryAccountId,
    },
    fetchPolicy: 'cache-and-network',
    skip: billingUnavailable || disabled,
  });

  const {
    data: { getChargeBeeSubscription: currentSubscription } = {},
    loading: loadingSubscription,
    refetch: refetchSubscription,
  } = useQuery(CUSTOMER_SUBSCRIPTION, {
    variables: {
      accountId: primaryAccountId,
    },
    fetchPolicy: 'cache-first',
    skip: billingUnavailable,
  });

  const openChargebeePortal = useCallback(
    (portalType, ...args) => {
      const portalTypes = {
        openSection: {
          openPortal: handlePortalOpenSection,
          baseArgs: [makeCustomerPortal, primaryAccountId],
        },
        planCheckout: {
          openPortal: handlePlanCheckoutSession,
          baseArgs: [makeCheckoutPortal],
        },
        planUpgrade: {
          openPortal: handlePlanUpgradeSession,
          baseArgs: [makeUpgradePortal, primaryAccountId],
        },
        oneTimeCheckout: {
          openPortal: handleOneTimeCheckout,
          baseArgs: [makeOneTimeCheckout],
        },
      };
      const { openPortal, baseArgs } = portalTypes[portalType];
      openPortal(...baseArgs, ...args);
    },
    [
      primaryAccountId,
      makeCheckoutPortal,
      makeCustomerPortal,
      makeOneTimeCheckout,
      makeUpgradePortal,
    ],
  );

  const loadingBilling = useMemo(
    () => loadingPlans || loadingPaymentSource || loadingSubscription,
    [loadingPlans, loadingPaymentSource, loadingSubscription],
  );

  const planBilling = useMemo(() => {
    if (
      checkFeaturePrimitiveValue({
        moduleName: 'billing',
        featureName: 'billingFormat',
        value: 'plan',
      }) &&
      usage
    ) {
      const hasRemainingExchanges =
        getRemainingExchanges(usage.numberOfExchangeAccounts, plan) > 0;
      const hasRemainingWallets =
        getRemainingWallets(usage.numberOfWallets, plan) > 0;

      return {
        hasRemainingExchanges,
        hasRemainingWallets,
      };
    }
    return {};
  }, [usage, plan]);

  const billingValue = useMemo(
    () => ({
      plans: plans || [],
      loadingBilling,
      paymentSource: paymentSource || {},
      currentSubscription,
      planBilling,
    }),
    [plans, loadingBilling, paymentSource, currentSubscription, planBilling],
  );

  const billingActionsValue = useMemo(
    () => ({
      openChargebeePortal,
      refetchPaymentSource,
      refetchSubscription,
    }),
    [openChargebeePortal, refetchPaymentSource, refetchSubscription],
  );

  return (
    <BillingContext.Provider value={billingValue}>
      <BillingActionsContext.Provider value={billingActionsValue}>
        {children}
      </BillingActionsContext.Provider>
    </BillingContext.Provider>
  );
};

export const BillingConsumer = BillingContext.Consumer;

BillingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
